import { useCallback } from 'react';
import { Button } from '@direct-frontend/components';
import { useGridColumnPinning, useGridRowSelectionState } from '@adv-frontend/grid';
import { ErrorBlankSad } from '#shared/components/ErrorBlank';
import { type SelectionSet, useGridColumnSorting, useGridRowEnhancedSelectionManager } from '#shared/components/grid';
import { type ProductVariantSortByKey } from '../api';

export const useProductVariantsAndCardsCommonGridProps = ({
    onResetFilters,
    sortBy,
    sortOrder,
    onSortChange,
    isSortByKey,
    blockForEmptyList,
    initialSelectedItems,
}: {
    onResetFilters: () => void;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
    isSortByKey: (key: string) => key is ProductVariantSortByKey;
    blockForEmptyList?: {
        controls?: boolean;
        title?: string;
        subtitle?: string;
    };
    initialSelectedItems?: SelectionSet<string>;
}) => {
    const renderEmpty = useCallback(() => {
        const {
            controls = true,
            title = 'По выбранным фильтрам ничего не найдено',
            subtitle = 'Попробуйте изменить фильтрацию или удалите полностью',
        } = blockForEmptyList ?? {};

        return (
            <ErrorBlankSad
                title={title}
                subtitle={subtitle}
                controls={
                    controls ? (
                        <Button size="m" color="normal" onClick={onResetFilters}>
                            Сбросить фильтры
                        </Button>
                    ) : null
                }
            />
        );
    }, [blockForEmptyList, onResetFilters]);

    const columnPinning = useGridColumnPinning({ left: ['select', 'expand', 'name'] });

    const columnSorting = useGridColumnSorting<ProductVariantSortByKey>({
        sortKey: sortBy,
        sortOrder,
        onSortChange,
        isValidSortKey: isSortByKey,
        defaultSort: { key: 'created', order: 'desc' },
    });

    const gridRowSelectionState = useGridRowSelectionState();
    const selectionManager = useGridRowEnhancedSelectionManager(initialSelectedItems);

    return {
        renderEmpty,
        columnPinning,
        columnSorting,
        gridRowSelectionState,
        selectionManager,
    };
};
